import type { BillingPlanList, BillingTier } from "@/lib/api/billing";
import { formatPrice } from "@/lib/money";
import { priceOf } from "./catalog";
import { PLAN_WORDS } from "./planWords";

/**
 * The upgrade sheet: the plans sold by the month, and the smallest pack,
 * from billing's plans. Pure.
 */

/** One plan on the upgrade sheet. */
export interface UpgradeCard {
    tier: BillingTier;
    name: string;
    price: string;
    line: string;
    /** The cheapest paid plan, framed as the one to take. */
    lead: boolean;
}

/** Each plan sold by the month for money, cheapest first. */
export function upgradeCards(plans: BillingPlanList | undefined): UpgradeCard[] {
    const paid = (plans?.plans ?? [])
        .map((plan) => ({ plan, month: priceOf(plan, "month") }))
        .filter((entry) => entry.month !== undefined)
        .sort((left, right) => (left.month?.unit_amount ?? 0) - (right.month?.unit_amount ?? 0));
    return paid.map(({ plan, month }, index) => {
        return {
            tier: plan.tier,
            name: plan.name,
            price: `${formatPrice(month?.unit_amount ?? 0, month?.currency ?? "usd")} a month`,
            line: `${plan.tailors_per_month} tailors a month, written by ${PLAN_WORDS[plan.tier].name}.`,
            lead: index === 0,
        };
    });
}

/** "Add a pack of 10 tailors, $10": the smallest pack on sale, or null when none is. */
export function packAsk(plans: BillingPlanList | undefined): string | null {
    const pack = [...(plans?.packs ?? [])].sort((left, right) => left.tailors - right.tailors)[0];
    return pack
        ? `Add a pack of ${pack.tailors} tailors, ${formatPrice(pack.unit_amount, pack.currency)}`
        : null;
}
